'use client';

import { useTenantOptional } from '@/contexts/TenantContext';
import { useCookieConsent } from '@/components/analytics/AnalyticsProvider';
import Link from 'next/link';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { X, Shield, BarChart3, Target, Settings2, ChevronRight } from 'lucide-react';

/**
 * Cookie categories matching GDPR requirements.
 * - essential: Always enabled, required for site functionality
 * - analytics: For tracking and analytics (Google Analytics, Clarity, etc.)
 * - marketing: For advertising and remarketing (Facebook Pixel, etc.)
 */
export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

const COOKIE_CONSENT_KEY = 'cookie-consent';
const COOKIE_PREFERENCES_KEY = 'cookie-preferences';

/**
 * Check if cookie consent has been given (for analytics)
 * Used by AnalyticsProvider to determine if tracking scripts should load
 */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;
  
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (consent === 'accepted') return true;
  
  const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (prefs) {
    try {
      const parsed = JSON.parse(prefs) as CookiePreferences;
      return parsed.analytics === true;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Check if marketing consent has been given
 */
export function hasMarketingConsent(): boolean {
  if (typeof window === 'undefined') return false;
  
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (consent === 'accepted') return true;
  
  const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (prefs) {
    try {
      const parsed = JSON.parse(prefs) as CookiePreferences;
      return parsed.marketing === true;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Theme-specific styling configuration
 */
interface ThemeStyles {
  // Main colors
  primaryGradient: string;
  primarySolid: string;
  accentColor: string;
  // Card styling
  cardBg: string;
  cardBorder: string;
  accentBar: string;
  // Text
  titleColor: string;
  textColor: string;
  mutedColor: string;
  // Buttons
  primaryBtnBg: string;
  primaryBtnShadow: string;
  secondaryBtnBorder: string;
  // Toggle
  toggleOnBg: string;
  toggleOnShadow: string;
  // Badge
  badgeBg: string;
  badgeColor: string;
  // Border radius style
  borderRadius: string;
}

function getThemeStyles(themeId: string | undefined): ThemeStyles {
  switch (themeId) {
    case 'stone-investment':
      // Luxury greyscale with minimal design
      return {
        primaryGradient: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
        primarySolid: '#1a1a1a',
        accentColor: '#8a8a8a',
        cardBg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.95) 100%)',
        cardBorder: 'rgba(0, 0, 0, 0.08)',
        accentBar: 'linear-gradient(90deg, #1a1a1a 0%, #4a4a4a 50%, #8a8a8a 100%)',
        titleColor: '#1a1a1a',
        textColor: '#4a4a4a',
        mutedColor: '#6b6b6b',
        primaryBtnBg: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
        primaryBtnShadow: '0 8px 24px -4px rgba(0, 0, 0, 0.25)',
        secondaryBtnBorder: '#d4d4d4',
        toggleOnBg: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
        toggleOnShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.3)',
        badgeBg: 'rgba(26, 26, 26, 0.08)',
        badgeColor: '#1a1a1a',
        borderRadius: '0.5rem', // Square corners for luxury feel
      };

    case 'allys':
      // Modern real estate with navy blue and gold accents
      return {
        primaryGradient: 'linear-gradient(135deg, #243462 0%, #1a2847 100%)',
        primarySolid: '#243462',
        accentColor: '#BBA339',
        cardBg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.97) 0%, rgba(248, 249, 250, 0.95) 100%)',
        cardBorder: 'rgba(36, 52, 98, 0.1)',
        accentBar: 'linear-gradient(90deg, #243462 0%, #BBA339 50%, #E4CE4A 100%)',
        titleColor: '#040813',
        textColor: '#374151',
        mutedColor: '#6B7280',
        primaryBtnBg: 'linear-gradient(135deg, #243462 0%, #1a2847 100%)',
        primaryBtnShadow: '0 8px 24px -4px rgba(36, 52, 98, 0.35)',
        secondaryBtnBorder: '#E5E7EB',
        toggleOnBg: 'linear-gradient(135deg, #243462 0%, #1a2847 100%)',
        toggleOnShadow: '0 4px 12px -2px rgba(36, 52, 98, 0.4)',
        badgeBg: 'rgba(187, 163, 57, 0.12)',
        badgeColor: '#8B7A2A',
        borderRadius: '1rem',
      };

    case 'royale':
    case 'route-royal':
      // Professional marketing with Oxford blue
      return {
        primaryGradient: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
        primarySolid: '#1E3A8A',
        accentColor: '#3B82F6',
        cardBg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.97) 0%, rgba(249, 250, 251, 0.95) 100%)',
        cardBorder: 'rgba(30, 58, 138, 0.1)',
        accentBar: 'linear-gradient(90deg, #1E3A8A 0%, #3B82F6 50%, #60A5FA 100%)',
        titleColor: '#111827',
        textColor: '#374151',
        mutedColor: '#6B7280',
        primaryBtnBg: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
        primaryBtnShadow: '0 8px 24px -4px rgba(30, 58, 138, 0.35)',
        secondaryBtnBorder: '#E5E7EB',
        toggleOnBg: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
        toggleOnShadow: '0 4px 12px -2px rgba(30, 58, 138, 0.4)',
        badgeBg: 'rgba(59, 130, 246, 0.1)',
        badgeColor: '#1E40AF',
        borderRadius: '0.75rem',
      };

    default:
      // Default theme with teal/modern styling
      return {
        primaryGradient: 'linear-gradient(135deg, var(--color-primary, #007A87) 0%, var(--color-secondary, #00A699) 100%)',
        primarySolid: 'var(--color-primary, #007A87)',
        accentColor: 'var(--color-accent, #00A699)',
        cardBg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 255, 255, 0.93) 100%)',
        cardBorder: 'rgba(0, 122, 135, 0.1)',
        accentBar: 'linear-gradient(90deg, var(--color-primary, #007A87) 0%, var(--color-secondary, #00A699) 100%)',
        titleColor: 'var(--color-text, #222222)',
        textColor: '#4B5563',
        mutedColor: '#6B7280',
        primaryBtnBg: 'linear-gradient(135deg, var(--color-primary, #007A87) 0%, var(--color-secondary, #00A699) 100%)',
        primaryBtnShadow: '0 8px 24px -4px rgba(0, 122, 135, 0.35)',
        secondaryBtnBorder: '#E5E7EB',
        toggleOnBg: 'linear-gradient(135deg, var(--color-primary, #007A87) 0%, var(--color-secondary, #00A699) 100%)',
        toggleOnShadow: '0 4px 12px -2px rgba(0, 122, 135, 0.4)',
        badgeBg: 'rgba(0, 122, 135, 0.08)',
        badgeColor: '#007A87',
        borderRadius: '1rem',
      };
  }
}

/**
 * Modern GDPR-compliant Cookie Consent Banner
 * 
 * Features:
 * - Compact, non-intrusive main banner
 * - Theme-adaptive styling (Allys, Stone Investment, Route Royal)
 * - Supports multiple locales (en, es, fr)
 * - Granular cookie preferences with elegant toggles
 * - Triggers AnalyticsProvider to load scripts on consent
 * - Professional, accessible design
 */
export default function CookieConsent() {
  const tenant = useTenantOptional();
  const locale = tenant?.locale || 'en';
  const themeId = tenant?.themeId;
  const { acceptConsent, rejectConsent } = useCookieConsent();
  
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    timestamp: '',
  });

  // Get theme-specific styles
  const theme = useMemo(() => getThemeStyles(themeId), [themeId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    
    if (consent === 'accepted' || consent === 'rejected') {
      setShowBanner(false);
      if (prefs) {
        try {
          setPreferences(JSON.parse(prefs));
        } catch {
          // Ignore parse errors
        }
      }
      return;
    }
    
    const timer = setTimeout(() => setShowBanner(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const triggerShake = useCallback(() => {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 600);
  }, []);

  const closeBanner = useCallback(() => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setShowBanner(false);
      setShowPreferences(false);
      setIsAnimatingOut(false);
    }, 300);
  }, []);

  const savePreferences = useCallback((prefs: CookiePreferences, accepted: boolean) => {
    const finalPrefs: CookiePreferences = {
      ...prefs,
      timestamp: new Date().toISOString(),
    };
    
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(finalPrefs));
    localStorage.setItem(COOKIE_CONSENT_KEY, accepted ? 'accepted' : 'rejected');

    // Google Consent Mode v2 — update consent signals immediately
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        ad_user_data: prefs.marketing ? 'granted' : 'denied',
        ad_personalization: prefs.marketing ? 'granted' : 'denied',
      });
    }

    // Meta consent integration
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('consent', prefs.marketing ? 'grant' : 'revoke');
    }
    
    if (accepted && (prefs.analytics || prefs.marketing)) {
      acceptConsent();
    } else {
      rejectConsent();
    }
    
    setPreferences(finalPrefs);
    closeBanner();
  }, [acceptConsent, rejectConsent, closeBanner]);

  const handleAcceptAll = useCallback(() => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: '',
    }, true);
  }, [savePreferences]);

  const handleRejectAll = useCallback(() => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: '',
    }, false);
  }, [savePreferences]);

  const handleSavePreferences = useCallback(() => {
    const accepted = preferences.analytics || preferences.marketing;
    savePreferences(preferences, accepted);
  }, [preferences, savePreferences]);

  const togglePreference = useCallback((type: keyof Omit<CookiePreferences, 'timestamp'>) => {
    if (type === 'essential') return;
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  }, []);

  const getTranslations = (loc: string): TranslationStrings => {
    if (loc === 'en' || loc === 'es' || loc === 'fr') {
      return translations[loc];
    }
    return translations.en;
  };
  const t = getTranslations(locale);

  if (!showBanner) return null;

  return (
    <>
      <style jsx global>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.6s cubic-bezier(.36,.07,.19,.97) both;
        }
      `}</style>

      {/* Blocking overlay to force interaction (Soft Wall) */}
      {!showPreferences && (
        <div 
          className="fixed inset-0 z-[9990] bg-transparent"
          onClick={triggerShake}
          aria-hidden="true"
        />
      )}

      {/* Backdrop for preferences modal */}
      {showPreferences && (
        <div 
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] transition-opacity duration-300 ${
            isAnimatingOut ? 'opacity-0' : 'opacity-100'
          }`}
          onClick={() => setShowPreferences(false)}
        />
      )}

      <div 
        className={`fixed bottom-0 left-0 right-0 z-[9999] p-3 sm:p-4 transition-all duration-400 ease-out ${
          isAnimatingOut 
            ? 'translate-y-full opacity-0' 
            : 'translate-y-0 opacity-100'
        } ${isShaking ? 'animate-shake' : ''}`}
        style={{ 
          animation: !isAnimatingOut && !isShaking ? 'slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)' : undefined 
        }}
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
      >
        <div className={`mx-auto ${showPreferences ? 'max-w-2xl' : 'max-w-5xl'}`}>
          {/* Glass Card Container */}
          <div 
            className="relative overflow-hidden border shadow-xl"
            style={{
              background: theme.cardBg,
              borderColor: theme.cardBorder,
              backdropFilter: 'blur(16px) saturate(180%)',
              boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
              borderRadius: theme.borderRadius,
            }}
          >
            {/* Decorative gradient accent bar */}
            <div 
              className="absolute top-0 left-0 right-0 h-0.5"
              style={{ background: theme.accentBar }}
            />

            <div className="relative">
              {!showPreferences ? (
                // Compact main consent banner
                <div className="px-4 sm:px-5 py-3 sm:py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div 
                          className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center"
                          style={{
                            background: theme.primaryGradient,
                            borderRadius: themeId === 'stone-investment' ? '0.375rem' : '0.625rem',
                            boxShadow: theme.primaryBtnShadow,
                          }}
                        >
                          <Shield className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 
                            id="cookie-consent-title" 
                            className="text-sm sm:text-base font-semibold tracking-tight"
                            style={{ color: theme.titleColor }}
                          >
                            {t.title}
                          </h3>
                          <p 
                            id="cookie-consent-description" 
                            className="text-xs sm:text-sm mt-0.5 line-clamp-2 sm:line-clamp-1"
                            style={{ color: theme.mutedColor }}
                          >
                            {t.shortDescription || t.description}
                          </p>
                        </div>
                      </div>
                      
                      {/* Policy links - hidden on mobile in compact view */}
                      <div className="hidden sm:flex items-center gap-3 mt-2 ml-12">
                        <Link
                          href={`/${locale}/privacy`}
                          className="inline-flex items-center gap-1 text-xs font-medium transition-colors hover:opacity-70"
                          style={{ color: theme.primarySolid }}
                        >
                          {t.privacyPolicy}
                          <ChevronRight className="w-3 h-3" />
                        </Link>
                        <span style={{ color: theme.cardBorder }}>•</span>
                        <Link
                          href={`/${locale}/cookies`}
                          className="inline-flex items-center gap-1 text-xs font-medium transition-colors hover:opacity-70"
                          style={{ color: theme.primarySolid }}
                        >
                          {t.cookiePolicy}
                          <ChevronRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>

                    {/* Action buttons - compact */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setShowPreferences(true)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-medium transition-all duration-200 hover:opacity-80"
                        style={{ 
                          color: theme.mutedColor,
                          background: 'rgba(0, 0, 0, 0.04)',
                          borderRadius: themeId === 'stone-investment' ? '0.375rem' : '0.5rem',
                        }}
                      >
                        <Settings2 className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">{t.customize}</span>
                      </button>
                      <button
                        onClick={handleRejectAll}
                        className="inline-flex items-center justify-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border transition-all duration-200 hover:opacity-80"
                        style={{ 
                          color: theme.textColor,
                          borderColor: theme.secondaryBtnBorder,
                          background: 'white',
                          borderRadius: themeId === 'stone-investment' ? '0.375rem' : '0.5rem',
                        }}
                      >
                        {t.rejectAll}
                      </button>
                      <button
                        onClick={handleAcceptAll}
                        className="inline-flex items-center justify-center px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                        style={{
                          background: theme.primaryBtnBg,
                          boxShadow: theme.primaryBtnShadow,
                          borderRadius: themeId === 'stone-investment' ? '0.375rem' : '0.5rem',
                        }}
                      >
                        {t.acceptAll}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Detailed preferences panel
                <div className="px-5 sm:px-6 py-5 sm:py-6">
                  <div className="relative">
                    <button
                      onClick={() => setShowPreferences(false)}
                      className="absolute right-0 top-0 p-1.5 transition-colors rounded-lg hover:bg-black/5"
                      style={{ color: theme.mutedColor }}
                      aria-label="Close preferences"
                    >
                      <X className="h-4 w-4" />
                    </button>

                    <div className="pr-8">
                      <h3 
                        className="text-lg sm:text-xl font-bold tracking-tight"
                        style={{ color: theme.titleColor }}
                      >
                        {t.preferencesTitle}
                      </h3>
                      <p 
                        className="text-sm mt-1"
                        style={{ color: theme.mutedColor }}
                      >
                        {t.preferencesDescription}
                      </p>
                    </div>

                    <div className="mt-5 space-y-2.5">
                      <CookieCategory
                        icon={<Shield className="h-4 w-4" />}
                        title={t.essential.title}
                        description={t.essential.description}
                        checked={preferences.essential}
                        disabled
                        required
                        theme={theme}
                        themeId={themeId}
                      />
                      <CookieCategory
                        icon={<BarChart3 className="h-4 w-4" />}
                        title={t.analytics.title}
                        description={t.analytics.description}
                        checked={preferences.analytics}
                        onChange={() => togglePreference('analytics')}
                        theme={theme}
                        themeId={themeId}
                      />
                      <CookieCategory
                        icon={<Target className="h-4 w-4" />}
                        title={t.marketing.title}
                        description={t.marketing.description}
                        checked={preferences.marketing}
                        onChange={() => togglePreference('marketing')}
                        theme={theme}
                        themeId={themeId}
                      />
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 mt-6 pt-5 border-t" style={{ borderColor: theme.cardBorder }}>
                      <button
                        onClick={() => setShowPreferences(false)}
                        className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium border transition-all duration-200 hover:opacity-80"
                        style={{ 
                          color: theme.textColor,
                          borderColor: theme.secondaryBtnBorder,
                          background: 'white',
                          borderRadius: themeId === 'stone-investment' ? '0.375rem' : '0.625rem',
                        }}
                      >
                        {t.cancel}
                      </button>
                      <button
                        onClick={handleSavePreferences}
                        className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                        style={{
                          background: theme.primaryBtnBg,
                          boxShadow: theme.primaryBtnShadow,
                          borderRadius: themeId === 'stone-investment' ? '0.375rem' : '0.625rem',
                        }}
                      >
                        {t.savePreferences}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Cookie category toggle with theme-aware styling
 */
function CookieCategory({
  icon,
  title,
  description,
  checked,
  disabled,
  required,
  onChange,
  theme,
  themeId,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  required?: boolean;
  onChange?: () => void;
  theme: ThemeStyles;
  themeId: string | undefined;
}) {
  const id = title.toLowerCase().replace(/\s+/g, '-');
  const isSquare = themeId === 'stone-investment';
  
  return (
    <label
      htmlFor={id}
      className={`group flex items-center gap-3 p-3 sm:p-4 border transition-all duration-200 ${
        disabled ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'
      }`}
      style={{
        background: checked && !disabled 
          ? `linear-gradient(135deg, ${theme.badgeBg} 0%, transparent 100%)`
          : disabled 
            ? 'rgba(0, 0, 0, 0.02)' 
            : 'white',
        borderColor: checked && !disabled ? theme.primarySolid + '30' : theme.cardBorder,
        borderRadius: isSquare ? '0.375rem' : '0.75rem',
      }}
    >
      {/* Icon container */}
      <div 
        className="shrink-0 w-9 h-9 flex items-center justify-center transition-all duration-200"
        style={{
          background: checked ? theme.primaryGradient : 'rgba(0, 0, 0, 0.05)',
          color: checked ? 'white' : theme.mutedColor,
          borderRadius: isSquare ? '0.25rem' : '0.5rem',
          boxShadow: checked ? theme.toggleOnShadow : 'none',
        }}
      >
        {icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span 
            className="text-sm font-medium"
            style={{ color: theme.titleColor }}
          >
            {title}
          </span>
          {required && (
            <span 
              className="text-[10px] font-medium px-1.5 py-0.5"
              style={{
                background: theme.badgeBg,
                color: theme.badgeColor,
                borderRadius: isSquare ? '0.25rem' : '9999px',
              }}
            >
              Required
            </span>
          )}
        </div>
        <p 
          className="text-xs mt-0.5 leading-relaxed"
          style={{ color: theme.mutedColor }}
        >
          {description}
        </p>
      </div>

      {/* Toggle switch */}
      <div className="shrink-0">
        <div className="relative">
          <input
            type="checkbox"
            id={id}
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            className="sr-only peer"
          />
          <div 
            className="w-11 h-6 transition-all duration-300"
            style={{
              background: checked ? theme.toggleOnBg : '#d1d5db',
              borderRadius: isSquare ? '0.375rem' : '9999px',
              boxShadow: checked ? theme.toggleOnShadow : 'inset 0 1px 2px rgba(0,0,0,0.1)',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <div 
              className="absolute top-0.5 w-5 h-5 bg-white shadow-md transition-all duration-300"
              style={{
                left: checked ? '22px' : '2px',
                borderRadius: isSquare ? '0.25rem' : '9999px',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
              }}
            />
          </div>
        </div>
      </div>
    </label>
  );
}

/**
 * Translation strings type
 */
interface TranslationStrings {
  title: string;
  description: string;
  shortDescription?: string;
  privacyPolicy: string;
  cookiePolicy: string;
  customize: string;
  rejectAll: string;
  acceptAll: string;
  preferencesTitle: string;
  preferencesDescription: string;
  cancel: string;
  savePreferences: string;
  essential: { title: string; description: string };
  analytics: { title: string; description: string };
  marketing: { title: string; description: string };
}

/**
 * Translations for supported locales
 */
const translations: { en: TranslationStrings; es: TranslationStrings; fr: TranslationStrings } = {
  en: {
    title: 'We value your privacy',
    description: 'We use cookies to enhance your browsing experience, serve personalised content, and analyse our traffic. You can choose which cookies to accept.',
    shortDescription: 'We use cookies for a better experience. Choose your preferences.',
    privacyPolicy: 'Privacy Policy',
    cookiePolicy: 'Cookie Policy',
    customize: 'Customise',
    rejectAll: 'Reject All',
    acceptAll: 'Accept All',
    preferencesTitle: 'Cookie Preferences',
    preferencesDescription: 'Manage your cookie preferences below. Essential cookies are required for the website to function properly.',
    cancel: 'Cancel',
    savePreferences: 'Save Preferences',
    essential: {
      title: 'Essential Cookies',
      description: 'Required for the website to function. These cannot be disabled.',
    },
    analytics: {
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website.',
    },
    marketing: {
      title: 'Marketing Cookies',
      description: 'Used to deliver personalised advertisements and measure their effectiveness.',
    },
  },
  es: {
    title: 'Valoramos tu privacidad',
    description: 'Utilizamos cookies para mejorar tu experiencia de navegación, ofrecer contenido personalizado y analizar nuestro tráfico. Puedes elegir qué cookies aceptar.',
    shortDescription: 'Usamos cookies para una mejor experiencia. Elige tus preferencias.',
    privacyPolicy: 'Política de Privacidad',
    cookiePolicy: 'Política de Cookies',
    customize: 'Personalizar',
    rejectAll: 'Rechazar Todo',
    acceptAll: 'Aceptar Todo',
    preferencesTitle: 'Preferencias de Cookies',
    preferencesDescription: 'Gestiona tus preferencias de cookies a continuación. Las cookies esenciales son necesarias para el funcionamiento del sitio web.',
    cancel: 'Cancelar',
    savePreferences: 'Guardar Preferencias',
    essential: {
      title: 'Cookies Esenciales',
      description: 'Necesarias para el funcionamiento del sitio web. No se pueden desactivar.',
    },
    analytics: {
      title: 'Cookies de Análisis',
      description: 'Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web.',
    },
    marketing: {
      title: 'Cookies de Marketing',
      description: 'Se utilizan para ofrecer anuncios personalizados y medir su efectividad.',
    },
  },
  fr: {
    title: 'Nous respectons votre vie privée',
    description: 'Nous utilisons des cookies pour améliorer votre expérience de navigation, proposer du contenu personnalisé et analyser notre trafic. Vous pouvez choisir les cookies à accepter.',
    shortDescription: 'Nous utilisons des cookies pour une meilleure expérience.',
    privacyPolicy: 'Politique de Confidentialité',
    cookiePolicy: 'Politique des Cookies',
    customize: 'Personnaliser',
    rejectAll: 'Tout Refuser',
    acceptAll: 'Tout Accepter',
    preferencesTitle: 'Préférences de Cookies',
    preferencesDescription: 'Gérez vos préférences de cookies ci-dessous. Les cookies essentiels sont nécessaires au bon fonctionnement du site.',
    cancel: 'Annuler',
    savePreferences: 'Enregistrer les Préférences',
    essential: {
      title: 'Cookies Essentiels',
      description: 'Nécessaires au fonctionnement du site. Ils ne peuvent pas être désactivés.',
    },
    analytics: {
      title: 'Cookies Analytiques',
      description: 'Nous aident à comprendre comment les visiteurs interagissent avec notre site.',
    },
    marketing: {
      title: 'Cookies Marketing',
      description: 'Utilisés pour diffuser des publicités personnalisées et mesurer leur efficacité.',
    },
  },
};

/**
 * Named export for layout import
 */
export { CookieConsent };

declare global {
  interface Window {
    gtag?: (...args: [string, ...unknown[]]) => void;
    fbq?: (command: string, event: string, params?: Record<string, unknown>) => void;
  }
}
